import { useEffect, useState } from 'react'
import { Button, Callout, Card, CardContent, CardHeader, DataTable, KeyValueList, StatusPill } from './ui'
import { callCenterSetupApi, type ContractLeadQualification } from '../api/callCenterSetup'
import {
  CONTRACT_FIELDS_CHANGED_EVENT,
  contractFieldsApi,
  type PrefillPreview,
} from '../api/dynamicFields'

/**
 * F-088 (TASK-0342): "Dati raccolti dal call center" nella pratica. Mostra le risposte di qualificazione dei lead
 * convertiti in questa pratica (legame: lead convertito → pratica), nell'ordine delle domande del questionario a cui
 * rispondevano. Nessun importo.
 * F-091: "Riporta nei campi della pratica" -> anteprima -> conferma. Si riportano le risposte con un campo
 * corrispondente (stesso codice, oppure la domanda scelta come origine del campo, F-098) e vuoto: un valore gia' presente non si sovrascrive mai. Se non ci sono risposte, o la lettura non è consentita, la sezione non compare.
 */
function fmtDateTime(s: string): string {
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString('it-IT', { timeZone: 'Europe/Rome' })
}

export function ContractCallCenterData({ contractId }: { contractId: number }) {
  const [data, setData] = useState<ContractLeadQualification | null>(null)

  useEffect(() => {
    let alive = true
    callCenterSetupApi.qualification.forContract(contractId)
      .then(r => { if (alive) setData(r) })
      .catch(() => { if (alive) setData(null) })
    return () => { alive = false }
  }, [contractId])

  const [preview, setPreview] = useState<PrefillPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'success' | 'danger' | 'info'; text: string } | null>(null)

  async function openPreview() {
    setBusy(true)
    setMsg(null)
    try {
      setPreview(await contractFieldsApi.prefillPreview(contractId))
    } catch (e) {
      setMsg({ kind: 'danger', text: e instanceof Error ? e.message : "Errore nel calcolo dell'anteprima" })
    } finally {
      setBusy(false)
    }
  }

  async function confirm() {
    setBusy(true)
    setMsg(null)
    try {
      const r = await contractFieldsApi.prefillApply(contractId)
      setPreview(null)
      setMsg({
        kind: r.written.length > 0 ? 'success' : 'info',
        text: r.written.length > 0
          ? `Riportati ${r.written.length} campi nella pratica.`
          : 'Nessun campo da riportare.',
      })
      if (r.written.length > 0) {
        window.dispatchEvent(new CustomEvent(CONTRACT_FIELDS_CHANGED_EVENT, { detail: { contractId } }))
      }
    } catch (e) {
      setMsg({ kind: 'danger', text: e instanceof Error ? e.message : 'Errore durante il riporto' })
    } finally {
      setBusy(false)
    }
  }

  if (!data || data.leads.length === 0) return null

  const applicable = preview?.items.filter(i => i.apply) ?? []
  const skipped = preview?.items.filter(i => !i.apply) ?? []

  return (
    <Card variant="outlined" className="mt-3">
      <CardHeader icon="support_agent" title="Dati raccolti dal call center" />
      <CardContent>
        {data.leads.map(lead => (
          <div className="mb-3" key={lead.leadId}>
            {data.leads.length > 1 && <div className="text-muted md-typescale-body-small mb-1">Lead #{lead.leadId}</div>}
            {lead.forms.map(form => (
              <div className="mb-2" key={form.formId}>
                <div className="md-typescale-emphasized-body-medium">
                  {form.formName ?? `Questionario #${form.formId}`}{' '}
                  <span className="text-muted md-typescale-body-small">v{form.formVersion} · {fmtDateTime(form.answeredAt)}</span>
                </div>
                <KeyValueList items={form.items.map(item => ({ label: item.label, value: item.value }))} />
              </div>
            ))}
          </div>
        ))}

        <div className="border-top pt-3">
          {msg && <Callout tone={msg.kind === 'danger' ? 'error' : msg.kind} role="status">{msg.text}</Callout>}
          {!preview && (
            <Button type="button" variant="outlined" size="sm" disabled={busy} onClick={() => void openPreview()}>
              {busy ? 'Calcolo...' : 'Riporta nei campi della pratica'}
            </Button>
          )}
          {preview && (
            <div>
              <div className="md-typescale-emphasized-body-medium mb-1">Anteprima del riporto</div>
              {!preview.canApply && (
                <Callout tone="warning">
                  In questo stato i campi della pratica non si possono modificare: l&apos;anteprima è solo informativa.
                </Callout>
              )}
              {preview.items.length === 0 ? (
                <div className="md-typescale-body-small text-muted mb-2">
                  Nessuna risposta ha un campo corrispondente (stesso codice o domanda scelta come origine) nella pratica.
                </div>
              ) : (
                <div className="mb-2">
                  <DataTable variant="plain" caption="Anteprima del riporto dal questionario ai campi">
                    <thead>
                      <tr><th scope="col">Campo</th><th scope="col">Origine</th><th scope="col">Esito</th></tr>
                    </thead>
                    <tbody>
                      {applicable.map(i => (
                        <tr key={i.fieldCode}>
                          <td>{i.fieldLabel}</td>
                          <td>Domanda: {i.questionLabel}</td>
                          <td><StatusPill tone="success">Verrà scritto</StatusPill> {i.value}</td>
                        </tr>
                      ))}
                      {skipped.map(i => (
                        <tr key={i.fieldCode} className="text-muted">
                          <td>{i.fieldLabel}</td>
                          <td>Domanda: {i.questionLabel}</td>
                          <td>Saltato: {i.skipReason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </div>
              )}
              <div className="d-flex gap-2">
                <Button
                  type="button" variant="tonal" size="sm"
                  disabled={busy || !preview.canApply || applicable.length === 0}
                  onClick={() => void confirm()}
                >
                  {busy ? 'Salvataggio...' : `Conferma (${applicable.length})`}
                </Button>
                <Button type="button" variant="text" size="sm" disabled={busy} onClick={() => setPreview(null)}>
                  Annulla
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
